import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import axiosRetry from 'axios-retry';
import {
	handleRenewError,
	redirectToSignIn,
	renewUser,
	userManager,
} from '../utils/oidc';

type AuthRetryRequestConfig = InternalAxiosRequestConfig & {
	_authRetry?: boolean;
	skipForbiddenRedirect?: boolean;
};

// Requests without their own timeout get one of these. Slow endpoints (AI
// generation, third-party lookups) opt out with `slowRequestConfig`.
const DEFAULT_REQUEST_TIMEOUT_MS = 20000;
const DEFAULT_TRANSFER_TIMEOUT_MS = 120000;

const RETRYABLE_METHODS = new Set(['get', 'head', 'options']);
const RETRYABLE_STATUSES = new Set([502, 503, 504]);

const getDefaultTimeout = (config: InternalAxiosRequestConfig) => {
	// Streamed responses (Tom) can legitimately run for minutes.
	if (config.timeout || config.responseType === 'stream') {
		return undefined;
	}
	const isTransfer =
		config.data instanceof FormData ||
		!!config.onUploadProgress ||
		config.responseType === 'blob';
	return isTransfer
		? DEFAULT_TRANSFER_TIMEOUT_MS
		: DEFAULT_REQUEST_TIMEOUT_MS;
};

// One quick retry, only for read-only methods that got no response (offline,
// timeout, reset) or a gateway error. Writes are never repeated automatically.
const isSafeToRetry = (error: AxiosError) => {
	const method = error.config?.method?.toLowerCase();
	if (
		!method ||
		!RETRYABLE_METHODS.has(method) ||
		error.code === 'ERR_CANCELED'
	) {
		return false;
	}
	return !error.response || RETRYABLE_STATUSES.has(error.response.status);
};

export default function interceptor(baseURL: string) {
	const axiosInstance = axios.create({
		baseURL,
		// Timeouts become ETIMEDOUT instead of the generic ECONNABORTED.
		transitional: { clarifyTimeoutError: true },
	});

	axiosRetry(axiosInstance, {
		retries: 1,
		retryDelay: () => 1000,
		retryCondition: isSafeToRetry,
		shouldResetTimeout: true,
	});

	axiosInstance.interceptors.request.use(async request => {
		const defaultTimeout = getDefaultTimeout(request);
		if (defaultTimeout) {
			request.timeout = defaultTimeout;
		}
		const user = await userManager.getUser();
		const token = user?.access_token;

		if (token) {
			request.headers.Authorization = `Bearer ${token}`;
		} else {
			delete request.headers.Authorization;
		}

		return request;
	});

	axiosInstance.interceptors.response.use(
		response => response,
		async (error: AxiosError) => {
			const { response, config } = error;

			// Routes without a client-side guard (e.g. LLC Vault) rely on this.
			if (response?.status === 403) {
				if (
					!(config as AuthRetryRequestConfig)?.skipForbiddenRedirect
				) {
					window.location.replace('/forbidden');
				}
				return Promise.reject(error);
			}

			if (response?.status !== 401 || !config?.url) {
				return Promise.reject(error);
			}

			const retryConfig = config as AuthRetryRequestConfig;
			if (retryConfig._authRetry) {
				return Promise.reject(error);
			}

			retryConfig._authRetry = true;

			const currentUser = await userManager.getUser();
			if (!currentUser) {
				await redirectToSignIn();
				return Promise.reject(error);
			}

			try {
				if (
					currentUser.access_token &&
					retryConfig.headers.Authorization !==
						`Bearer ${currentUser.access_token}`
				) {
					retryConfig.headers.Authorization = `Bearer ${currentUser.access_token}`;
					return axiosInstance.request(retryConfig);
				}

				const renewedUser = await renewUser();
				if (!renewedUser?.access_token) {
					await redirectToSignIn(true);
					return Promise.reject(error);
				}

				retryConfig.headers.Authorization = `Bearer ${renewedUser.access_token}`;
				return axiosInstance.request(retryConfig);
			} catch (refreshError) {
				await handleRenewError(refreshError);

				return Promise.reject(error);
			}
		},
	);

	return axiosInstance;
}
